import { supabase } from '@/integrations/supabase/client';
import { SearchBuilder } from '../core/SearchBuilder';
import { SearchFilters, SearchResult } from '../core/types';

export class SequenceSearch extends SearchBuilder {
  constructor(filters: SearchFilters) {
    super(filters);
  }

  protected buildBaseQuery() {
    return supabase
      .from('display_songs')
      .select(`
        id,
        title,
        artist,
        genre,
        sequence_price,
        duration,
        year_introduced,
        sequence_available,
        album_cover_url,
        created_at,
        display_year:display_years(
          id,
          year,
          display:displays(
            id,
            name,
            location,
            holiday_type,
            display_type
          )
        )
      `)
      .eq('sequence_available', true);
  }

  /**
   * Apply sequence-specific filters
   */
  private applySequenceFilters(query: ReturnType<typeof supabase.from>): ReturnType<typeof supabase.from> {
    // Apply genre filters
    if (this.filters.genres && this.filters.genres.length > 0) {
      query = query.in('genre', this.filters.genres);
    }

    // Apply software type filters (if needed in future)
    if (this.filters.softwareTypes && this.filters.softwareTypes.length > 0) {
      // This would require additional database schema changes
      // For now, we'll skip this filter
    }

    // Apply price filters
    if (this.filters.priceRange?.min !== undefined) {
      query = query.gte('sequence_price', this.filters.priceRange.min);
    }
    if (this.filters.priceRange?.max !== undefined) {
      query = query.lte('sequence_price', this.filters.priceRange.max);
    }

    // Apply year filters
    if (this.filters.yearStarted?.min !== undefined) {
      query = query.gte('year_introduced', this.filters.yearStarted.min);
    }
    if (this.filters.yearStarted?.max !== undefined) {
      query = query.lte('year_introduced', this.filters.yearStarted.max);
    }

    // Apply channel count filters (if needed in future)
    if (this.filters.channelCount?.min !== undefined || this.filters.channelCount?.max !== undefined) {
      // This would require additional database schema changes
      // For now, we'll skip this filter
    }

    return query;
  }

  /**
   * Override text search for sequences to search title and artist
   */
  protected applyTextSearch(query: ReturnType<typeof supabase.from>): ReturnType<typeof supabase.from> {
    if (this.filters.query && this.filters.query.trim()) {
      const searchTerm = this.filters.query.trim();
      return query.or(`title.ilike.%${searchTerm}%,artist.ilike.%${searchTerm}%`);
    }
    return query;
  }

  /**
   * Filter sequences by required props
   */
  private async filterSequencesByProps(sequences: SearchResult[], props: string[]): Promise<SearchResult[]> {
    if (sequences.length === 0 || props.length === 0) return sequences;

    try {
      // Get sequences that have all the required props
      const { data, error } = await supabase.rpc('get_sequences_by_props', {
        prop_names: props
      });

      if (error) {
        console.error('Error filtering by props:', error);
        return sequences; // Return unfiltered results if props filtering fails
      }

      const validSequenceIds = new Set((data || []).map((row: Record<string, unknown>) => row.sequence_id));
      
      return sequences.filter(sequence => validSequenceIds.has(sequence.id));
    } catch (error) {
      console.error('Error in filterSequencesByProps:', error);
      return sequences;
    }
  }

  /**
   * Transform database results to SearchResult format
   */
  private transformSequenceResults(sequences: Record<string, unknown>[]): SearchResult[] {
    return sequences.map((sequence) => {
      const displayYear = sequence.display_year as Record<string, unknown> | null;
      const display = displayYear?.display as Record<string, unknown> | null;

      const result: SearchResult = {
        id: sequence.id as string,
        type: 'sequence',
        title: sequence.title as string,
        description: `A synchronized light show featuring ${sequence.title} by ${sequence.artist}`,
        imageUrl: (sequence.image_url as string) || (sequence.album_cover_url as string) || '',
        rating: 4.5, // Default rating - could be calculated from reviews
        location: display?.location as string,
        price: sequence.sequence_price as number,
        genre: sequence.genre as string,
        softwareType: 'xLights', // Default software type
        yearStarted: sequence.year_introduced as number,
        createdAt: sequence.created_at as string,
        relevanceScore: this.calculateRelevanceScore(sequence)
      };

      // Calculate distance if user location and display location are available
      if (this.filters.location && display?.latitude && display?.longitude) {
        result.distance = this.calculateDistance(
          this.filters.location.lat,
          this.filters.location.lng,
          display.latitude as number,
          display.longitude as number
        );
      }

      return result;
    });
  }

  /**
   * Calculate sequence-specific relevance score
   */
  protected calculateRelevanceScore(sequence: Record<string, unknown>): number {
    let score = 0;

    // Base rating score (using default since we don't have ratings yet)
    score += 4.5 * 20; // 90 points from default rating

    // Price appeal (lower prices are more appealing)
    const price = sequence.sequence_price as number || 0;
    if (price === 0) {
      score += 50; // Free sequences get bonus points
    } else {
      score += Math.max(0, 30 - (price / 2)); // Up to 30 points based on price
    }

    // Text match boost
    if (this.filters.query) {
      const query = this.filters.query.toLowerCase();
      const title = (sequence.title as string || '').toLowerCase();
      const artist = (sequence.artist as string || '').toLowerCase();
      
      if (title.includes(query)) {
        score += 50;
      }
      if (artist.includes(query)) {
        score += 30;
      }
      if (title.startsWith(query)) {
        score += 25;
      }
    }

    // Genre match boost
    if (this.filters.genres && sequence.genre) {
      if (this.filters.genres.includes(sequence.genre as string)) {
        score += 25;
      }
    }

    // Recent sequences get a small boost
    const createdAt = new Date(sequence.created_at as string).getTime();
    const now = Date.now();
    const daysSinceCreation = (now - createdAt) / (1000 * 60 * 60 * 24);
    const recencyBoost = Math.max(0, 10 - (daysSinceCreation / 30)); // Small boost for recent sequences
    score += recencyBoost;

    return Math.round(score);
  }

  /**
   * Execute the sequence search
   */
  public async execute(): Promise<SearchResult[]> {
    try {
      let query = this.buildBaseQuery();

      // Apply all filters
      query = this.applyTextSearch(query);
      query = this.applySequenceFilters(query);
      query = this.applySorting(query);
      query = this.applyPagination(query);

      const { data, error } = await query;

      if (error) {
        console.error('Sequence search error:', error);
        throw new Error(`Sequence search failed: ${error.message}`);
      }

      let results = this.transformSequenceResults(data || []);

      // Apply props filtering if specified
      if (this.filters.props && this.filters.props.length > 0) {
        results = await this.filterSequencesByProps(results, this.filters.props);
      }

      return results;
    } catch (error) {
      console.error('Sequence search execution error:', error);
      throw error;
    }
  }
}